import { signal } from '@preact/signals-core';
import {
  getAuthHeaders,
  assetsListSignal,
  customersSignal,
  sitesSignal,
  deviceModelsSignal,
  pageTypesSignal
} from './assets';
import { consumablesSignal, refreshConsumables } from './device_models';
import { escapeHtml } from '../utils/sanitize';
import '../components/analytics-graph';

export const selectedAnalyticsAssetIdSignal = signal<string | null>(null);
export const deviceAnalyticsDataSignal = signal<any | null>(null);
export const deviceAnalyticsLoadingSignal = signal<boolean>(false);
export const deviceAnalyticsErrorSignal = signal<string | null>(null);
export const graphViewModeSignal = signal<'realtime' | 'cumulative'>('realtime');
export const activeDetailTabSignal = signal<string>('consumables');

export function initDeviceAnalyticsSignals() {
	deviceAnalyticsDataSignal.value = null;
	deviceAnalyticsErrorSignal.value = null;
	graphViewModeSignal.value = 'realtime';
	activeDetailTabSignal.value = 'consumables';
}

export async function refreshDeviceAnalytics(assetId: string) {
	if (!assetId) return;
	selectedAnalyticsAssetIdSignal.value = assetId;

	deviceAnalyticsLoadingSignal.value = true;
	deviceAnalyticsErrorSignal.value = null;

	try {
		const res = await fetch(`/api/v1/assets/${assetId}/analytics`, {
			headers: getAuthHeaders(),
		});

		if (res.ok) {
			const data = await res.json();
			if (!data.isMps) {
				console.warn(`[Router] Asset ${assetId} is a Non-MPS device. Redirecting to non-mps-device-detail...`);
				window.location.hash = `#non-mps-device-detail?id=${assetId}`;
				return;
			}
			if (consumablesSignal.value.length === 0) {
				await refreshConsumables();
			}
			deviceAnalyticsDataSignal.value = data;

			// Populate dynamic sub-components once data is updated
			setTimeout(() => {
				const graph = document.getElementById('device-financial-graph') as any;
				if (graph) {
					graph.items = data.monthlyMetrics || [];
				}
				populateActiveTab(assetId, data);
			}, 50);
		} else {
			let errMsg = 'Failed to fetch device analytics.';
			try {
				const text = await res.text();
				if (text) {
					try {
						const errData = JSON.parse(text);
						errMsg = errData.error || errData.message || errMsg;
					} catch {
						errMsg = text;
					}
				} else {
					if (res.status === 404) {
						errMsg = 'Device not found.';
					} else if (res.status === 403) {
						errMsg = 'Access denied. You do not have permission to view this device.';
					} else if (res.status === 401) {
						errMsg = 'Session expired. Please log in again.';
					}
				}
			} catch (readErr) {
				console.error('Failed to read error response:', readErr);
			}
			deviceAnalyticsErrorSignal.value = errMsg;
			deviceAnalyticsDataSignal.value = null;
		}
	} catch (err) {
		console.error('Failed to query device analytics', err);
		deviceAnalyticsErrorSignal.value = err instanceof Error ? err.message : 'Error: network failure.';
		deviceAnalyticsDataSignal.value = null;
	} finally {
		deviceAnalyticsLoadingSignal.value = false;
	}
}

interface ConsumableProjection {
  consumableCode: string;
  consumableName: string;
  /** Currently installed? */
  isActive: boolean;
  /** Pages printed since install */
  pagesSinceInstall: number;
  /** Rated yield (from manufacturer or historical average) */
  ratedYield: number;
  /** Daily consumption rate (pages/day) */
  dailyRate: number;
  /** Estimated remaining pages */
  remainingPages: number;
  /** Estimated remaining days */
  remainingDays: number;
  /** Projected exhaustion date */
  projectedExhaustionDate: string;
  /** Usage percentage (0-100) */
  usagePercent: number;
  /** How the projection was calculated */
  methodDescription: string;
}

export function computeConsumableProjections(data: any): ConsumableProjection[] {
  const history = data.consumableHistory || [];
  const dailyAvg = data.dailyAverageVolume || 0;
  
  // Group by consumable code to get active + historical data
  const activeConsumables = history.filter((h: any) => !h.endDate);
  
  return activeConsumables.map((active: any) => {
    const pagesSinceInstall = (data.currentPageCount || 0) - (active.startPageCount || 0);
    const installDate = new Date(active.installDate);
    const daysSinceInstall = Math.max(1, Math.floor((Date.now() - installDate.getTime()) / (1000 * 60 * 60 * 24)));
    
    // Calculate daily rate from this specific consumable's usage
    const dailyRate = pagesSinceInstall / daysSinceInstall;
    
    // Get rated yield from historical averages of same consumable type
    const sameTypeHistory = history.filter((h: any) => 
      h.consumableCode === active.consumableCode && h.yieldPages && h.yieldPages > 0
    );
    
    // Also lookup in catalog
    const consumableCatalogItem = (consumablesSignal.value || []).find((c: any) => c.code === active.consumableCode);
    const mfgYield = consumableCatalogItem ? consumableCatalogItem.printYield : 0;
    
    let ratedYield: number;
    let methodDescription: string;
    
    if (sameTypeHistory.length >= 2) {
      // Use historical average yield from this machine's own data
      const avgYield = sameTypeHistory.reduce((sum: number, h: any) => sum + h.yieldPages, 0) / sameTypeHistory.length;
      ratedYield = Math.round(avgYield);
      methodDescription = `Based on ${sameTypeHistory.length} previous installations of ${active.consumableName} on this machine, which averaged ${ratedYield.toLocaleString()} pages per unit.`;
    } else if (mfgYield > 0) {
      // Use manufacturer rated yield
      ratedYield = mfgYield;
      methodDescription = `Using manufacturer rated yield of ${ratedYield.toLocaleString()} pages. Insufficient local history (< 2 previous installations) for a machine-specific average.`;
    } else {
      // Fallback: estimate from fleet daily average
      ratedYield = Math.round(dailyAvg * 90); // ~3 months as rough estimate
      methodDescription = `No historical yield data available. Estimated at ~90 days based on fleet daily volume of ${Math.round(dailyAvg)} pages/day.`;
    }
    
    const remainingPages = Math.max(0, ratedYield - pagesSinceInstall);
    const remainingDays = dailyRate > 0 ? Math.round(remainingPages / dailyRate) : 999;
    const exhaustionDate = new Date(Date.now() + remainingDays * 24 * 60 * 60 * 1000);
    const usagePercent = ratedYield > 0 ? Math.min(100, Math.round((pagesSinceInstall / ratedYield) * 100)) : 0;
    
    return {
      consumableCode: active.consumableCode,
      consumableName: active.consumableName,
      isActive: true,
      pagesSinceInstall,
      ratedYield,
      dailyRate: Math.round(dailyRate * 10) / 10,
      remainingPages,
      remainingDays,
      projectedExhaustionDate: exhaustionDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      usagePercent,
      methodDescription,
    };
  });
}

export function renderDeviceAnalyticsPage(): string {
	const loading = deviceAnalyticsLoadingSignal.value;
	const error = deviceAnalyticsErrorSignal.value;
	const data = deviceAnalyticsDataSignal.value;

	if (loading) {
		return `
			<s-page heading="Asset Detail" inlineSize="large">
				<s-stack gap="base" alignment="center" style="padding: 5rem 0;">
					<s-spinner size="large"></s-spinner>
					<s-text tone="subdued">Aggregating telemetry and compiling proportional rollups...</s-text>
				</s-stack>
			</s-page>
		`;
	}

	if (error) {
		return `
			<s-page heading="Asset Detail" inlineSize="large">
				<div style="padding: 1rem 0;">
					<div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem;">
						<a href="#assets" style="color: var(--p-color-text-link, #008060); text-decoration: none; font-size: 0.875rem;">&larr; Back to Asset Explorer</a>
					</div>
					<s-banner tone="critical" title="Calculation Error">
						<p>${error}</p>
					</s-banner>
				</div>
			</s-page>
		`;
	}

	if (!data) {
		return `
			<s-page heading="Asset Detail" inlineSize="large">
				<div style="padding: 1rem 0;">
					<div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem;">
						<a href="#assets" style="color: var(--p-color-text-link, #008060); text-decoration: none; font-size: 0.875rem;">&larr; Back to Asset Explorer</a>
					</div>
					<s-banner tone="attention" title="Asset Unavailable">
						<p>No analytics data loaded. Please select an asset from the Asset Explorer.</p>
					</s-banner>
				</div>
			</s-page>
		`;
	}

	// Enrich metadata client-side
	const assetId = selectedAnalyticsAssetIdSignal.value;
	const asset = (assetsListSignal.value || []).find((a: any) => a.id === assetId);

	let serialNumber = 'Unknown';
	let modelName = 'Unknown';
	let siteName = 'Unknown';
	let ownershipType = 'Unknown';
	let status = 'Unknown';
	let customerName = 'Unknown';

	if (asset) {
		serialNumber = asset.serialNumber || 'Unknown';
		status = asset.status || 'Unknown';
		ownershipType = asset.ownershipType || 'Unknown';

		const modelObj = (deviceModelsSignal.value || []).find((m: any) => m.id === asset.modelId);
		if (modelObj) {
			modelName = `${modelObj.vendor} ${modelObj.modelName}`;
		}

		const siteObj = (sitesSignal.value || []).find((s: any) => s.id === asset.siteId);
		if (siteObj) {
			siteName = siteObj.name;
		}

		const customerObj = (customersSignal.value || []).find((c: any) => c.id === asset.customerId);
		if (customerObj) {
			customerName = customerObj.name;
		}
	}

	const volumeStr = data.currentPageCount.toLocaleString() + ' pages';
	const avgVolumeStr = Math.round(data.dailyAverageVolume).toLocaleString() + ' pages/day';

	// Forecast service projected date
	const forecast = data.nextServiceForecast || {};
	const forecastDate = forecast.projectedDate ? new Date(forecast.projectedDate) : new Date();
	const forecastLabel = forecast.projectedDate ? forecastDate.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	}) : 'N/A';

	const isFallback = forecast.isFallback;
	const viewMode = graphViewModeSignal.peek();
	const activeDetailTab = activeDetailTabSignal.value;

	let statusTone = 'info';
	if (status === 'active') statusTone = 'success';
	if (status === 'error') statusTone = 'critical';

	let tabContentHtml = '';
	if (activeDetailTab === 'consumables') {
		const projections = computeConsumableProjections(data);
		
		const projectionsHtml = projections.length === 0 ? `
			<s-banner tone="attention">
				<p>No active consumables tracked for this device. Install consumables to see projections.</p>
			</s-banner>
		` : projections.map(p => {
			const badgeTone = p.usagePercent >= 80 ? 'critical' : p.usagePercent >= 60 ? 'attention' : 'success';
			const barColor = p.usagePercent >= 80 ? '#d72c0d' : p.usagePercent >= 60 ? '#b98900' : '#008060';
			const remainingColor = p.remainingDays <= 14 ? 'var(--p-color-text-critical, #d72c0d)' : 'var(--p-color-text, #202223)';
			const urgencyText = p.remainingDays <= 14 
				? ' — <span style="color:var(--p-color-text-critical, #d72c0d);font-weight:600;">Order replacement now</span>' 
				: p.remainingDays <= 30 
					? ' — <span style="color:var(--p-color-text-attention, #b98900);font-weight:600;">Order within 2 weeks</span>' 
					: '';

			return `
				<s-box padding="base" background="surface-secondary" border="base" borderRadius="base" style="margin-bottom:1rem;">
					<s-stack gap="base">
						<!-- Consumable Header -->
						<div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.5rem;">
							<s-stack gap="none">
								<s-text style="font-weight:600;font-size:1.05rem;">${p.consumableName}</s-text>
								<s-text tone="subdued" style="font-size:0.8rem;">Part: ${p.consumableCode}</s-text>
							</s-stack>
							<s-badge tone="${badgeTone}">
								${p.usagePercent}% Used
							</s-badge>
						</div>
						
						<!-- Usage Progress Bar -->
						<div style="width:100%;height:8px;background:var(--p-color-bg-surface, #ffffff);border-radius:4px;overflow:hidden;border:1px solid var(--p-color-border-subdued, #e1e3e5);">
							<div style="width:${p.usagePercent}%;height:100%;background:${barColor};border-radius:4px;transition:width 0.3s ease;"></div>
						</div>
						
						<!-- Metrics Grid -->
						<s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Pages Printed</s-text>
								<s-text style="font-weight:600;font-size:0.95rem;">${p.pagesSinceInstall.toLocaleString()}</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Rated Yield</s-text>
								<s-text style="font-weight:600;font-size:0.95rem;">${p.ratedYield.toLocaleString()} pgs</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Daily Rate</s-text>
								<s-text style="font-weight:600;font-size:0.95rem;">${p.dailyRate} pgs/day</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Est. Remaining</s-text>
								<s-text style="font-weight:600;font-size:0.95rem;color:${remainingColor};">
									${p.remainingDays} days
								</s-text>
							</s-stack>
						</s-grid>
						
						<!-- Projected Exhaustion -->
						<div style="display:flex;align-items:center;gap:0.5rem;padding:0.5rem;background:var(--p-color-bg-surface, #ffffff);border-radius:4px;border:1px solid var(--p-color-border-subdued, #e1e3e5);">
							<s-text style="font-size:0.875rem;">
								Projected exhaustion: <strong>${p.projectedExhaustionDate}</strong>${urgencyText}
							</s-text>
						</div>
						
						<!-- Methodology Disclosure -->
						<details style="margin-top:0.25rem;">
							<summary style="cursor:pointer;font-size:0.8rem;font-weight:500;color:var(--p-color-text-link, #008060);">
								How was this projection calculated?
							</summary>
							<div style="margin-top:0.5rem;padding:0.75rem;background:var(--p-color-bg-surface, #ffffff);border-radius:4px;font-size:0.8rem;line-height:1.5;color:var(--p-color-text-subdued);border:1px solid var(--p-color-border-subdued, #e1e3e5);">
								<p style="margin-bottom:0.5rem;">${p.methodDescription}</p>
								<p style="margin-bottom:0.5rem;">The daily consumption rate of <strong>${p.dailyRate} pages/day</strong> is calculated from ${p.pagesSinceInstall.toLocaleString()} pages printed since installation, divided by the number of days since install.</p>
								<p>Remaining pages (${p.remainingPages.toLocaleString()}) ÷ daily rate (${p.dailyRate}) = <strong>${p.remainingDays} days</strong> until projected exhaustion on <strong>${p.projectedExhaustionDate}</strong>.</p>
							</div>
						</details>
					</s-stack>
				</s-box>
			`;
		}).join('');

		const deliverySuggestionHtml = projections.length === 0 ? '' : `
			<s-box padding="base" background="surface" border="base" borderRadius="base" 
						 style="border-left:4px solid var(--p-color-border-accent, #008060); margin-top:1rem; margin-bottom:1.5rem;">
				<s-stack gap="small">
					<s-heading element="h3">📦 Next Suggested Delivery</s-heading>
					<s-text tone="subdued">Based on projected exhaustion dates and a 14-day lead time, order the following consumables:</s-text>
					
					<s-table>
						<s-table-header-row>
							<s-table-header>Consumable</s-table-header>
							<s-table-header>Part Code</s-table-header>
							<s-table-header>Order By</s-table-header>
							<s-table-header>Urgency</s-table-header>
						</s-table-header-row>
						<s-table-body>
							${projections.map(p => {
								const orderByDate = new Date(Date.now() + Math.max(0, p.remainingDays - 14) * 24 * 60 * 60 * 1000);
								const orderByStr = orderByDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
								const urgencyTone = p.remainingDays <= 14 ? 'critical' : p.remainingDays <= 30 ? 'attention' : 'success';
								const urgencyLabel = p.remainingDays <= 14 ? 'URGENT' : p.remainingDays <= 30 ? 'SOON' : 'PLANNED';
								
								return `
									<s-table-row>
										<s-table-cell><strong>${p.consumableName}</strong></s-table-cell>
										<s-table-cell><span style="font-family:monospace;font-size:0.85rem;">${p.consumableCode}</span></s-table-cell>
										<s-table-cell>${orderByStr}</s-table-cell>
										<s-table-cell><s-badge tone="${urgencyTone}">${urgencyLabel}</s-badge></s-table-cell>
									</s-table-row>
								`;
							}).join('')}
						</s-table-body>
					</s-table>
					
					<s-text tone="subdued" style="font-size:0.75rem;font-style:italic;">
						Delivery dates assume a 14-day lead time from order to installation. Adjust based on your supplier's actual delivery schedule.
					</s-text>
				</s-stack>
			</s-box>
		`;

		tabContentHtml = `
			<s-box padding="base" background="surface" border="base" borderRadius="base" style="margin-bottom: 1.5rem;">
				<s-stack gap="base">
					<s-stack gap="none">
						<s-heading element="h3">Active Consumable Projections</s-heading>
						<s-text tone="subdued">Estimated remaining life and suggested reorder timing for currently installed consumables.</s-text>
					</s-stack>
					${projectionsHtml}
				</s-stack>
			</s-box>

			${deliverySuggestionHtml}

			<s-box padding="none" background="surface" border="base" borderRadius="base">
				<div style="padding: 1rem 1rem 0.5rem 1rem;">
					<s-heading element="h3">Consumable Yield History</s-heading>
					<s-text tone="subdued">Tracks active and historical consumable replacements, installation counters, and actual print yield pages.</s-text>
				</div>
				<opm-data-table
					id="device-yield-table"
					searchable="false"
					paginated="true"
					page-size="5"
					empty-message="No consumable installations recorded for this device."
				></opm-data-table>
			</s-box>
		`;
	} else if (activeDetailTab === 'readings') {
		tabContentHtml = `
			<s-box padding="none" background="surface" border="base" borderRadius="base">
				<div style="padding: 1rem 1rem 0.5rem 1rem;">
					<s-heading element="h3">Reading History</s-heading>
					<s-text tone="subdued">Chronological log of recorded telemetry and physical meter readings.</s-text>
				</div>
				<opm-data-table
					id="asset360-readings-table"
					searchable="false"
					paginated="true"
					page-size="10"
					empty-message="No readings recorded for this device."
				></opm-data-table>
			</s-box>
		`;
	} else if (activeDetailTab === 'memos') {
		tabContentHtml = `
			<s-box padding="none" background="surface" border="base" borderRadius="base">
				<div style="padding: 1rem 1rem 0.5rem 1rem;">
					<s-heading element="h3">Linked Memos</s-heading>
					<s-text tone="subdued">Internal revenue and maintenance cost memos linked to this asset.</s-text>
				</div>
				<opm-data-table
					id="asset360-memos-table"
					searchable="false"
					paginated="true"
					page-size="5"
					empty-message="No memos linked to this asset."
				></opm-data-table>
			</s-box>
		`;
	} else if (activeDetailTab === 'service') {
		tabContentHtml = `
			<s-stack gap="base">
				<!-- 1. Asset Health & Metrics Panel -->
				<div id="asset-health-metrics-panel">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="base" alignment="center" style="padding:1rem 0;">
							<s-spinner size="small"></s-spinner>
							<s-text tone="subdued">Loading health KPIs and service details...</s-text>
						</s-stack>
					</s-box>
				</div>

				<!-- 2. Chronological Service Timeline -->
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<div style="margin-bottom: 1rem;">
						<s-heading element="h3">Chronological Service History</s-heading>
						<s-text tone="subdued">Timeline of tickets, technician sessions, and part replacement events.</s-text>
					</div>
					<div id="asset360-service-timeline-container">
						<s-stack gap="base" alignment="center" style="padding:1rem 0;">
							<s-spinner size="small"></s-spinner>
							<s-text tone="subdued">Generating service timeline...</s-text>
						</s-stack>
					</div>
				</s-box>

				<!-- 3. System Audit Logs Accordion -->
				<details style="margin-top: 1rem; border: 1px solid var(--p-color-border-subdued); border-radius: 4px; background: var(--p-color-bg-surface);">
					<summary style="padding: 1rem; font-weight: 600; cursor: pointer; user-select: none;">
						System Audit Trail (Go-Migrate & RLS Audit Logs)
					</summary>
					<div style="padding: 0 1rem 1rem 1rem;">
						<opm-data-table
							id="asset360-service-table"
							searchable="false"
							paginated="true"
							page-size="10"
							empty-message="No raw audit history entries for this asset."
						></opm-data-table>
					</div>
				</details>
			</s-stack>
		`;
	} else if (activeDetailTab === 'contract') {
		tabContentHtml = `
			<div id="asset360-contract-content">
				<s-stack gap="base" alignment="center" style="padding:2rem 0;">
					<s-spinner size="small"></s-spinner>
					<s-text tone="subdued">Loading contract terms...</s-text>
				</s-stack>
			</div>
		`;
	}

	return `
		<s-page heading="${serialNumber} — Asset Detail" inlineSize="large">
			<div style="padding: 0.25rem 0 5rem 0;">
				<!-- Breadcrumbs back link -->
				<div style="display:flex; align-items:center; gap:0.5rem; margin-bottom:1rem; flex-wrap:wrap;">
					<a href="#assets" style="color:var(--p-color-text-link, #008060); text-decoration:none; font-size:0.875rem; font-weight:500;">Asset Explorer</a>
					<span style="color:var(--p-color-text-subdued); font-size:0.875rem;">&gt;</span>
					<s-text style="font-weight:600; font-size:0.875rem;">${serialNumber}</s-text>
					<s-badge tone="${statusTone}">${status.toUpperCase()}</s-badge>
				</div>

				<!-- Asset Identity Header -->
				<s-section>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Customer</s-text>
								<s-text style="font-weight:600; font-size:0.95rem; color:var(--p-color-text);">${customerName}</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Model Class</s-text>
								<s-text style="font-weight:600; font-size:0.95rem; color:var(--p-color-text);">${modelName}</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Location / Site</s-text>
								<s-text style="font-weight:600; font-size:0.95rem; color:var(--p-color-text);">${siteName}</s-text>
							</s-stack>
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Ownership</s-text>
								<s-text style="font-weight:600; font-size:0.95rem; color:var(--p-color-text);">${ownershipType.toUpperCase()}</s-text>
							</s-stack>
						</s-grid>
					</s-box>
				</s-section>

				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="base" style="margin-top: 1.5rem;">
					<!-- Card 1: Current Volume -->
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text color="subdued" style="font-weight: 500; font-size: 0.875rem; text-transform: uppercase;">Current Volume</s-text>
							<s-heading element="h2" style="font-size: 1.75rem; font-weight: 700; color: var(--p-color-text);">${volumeStr}</s-heading>
							<s-text tone="subdued" style="font-size: 0.825rem;">Cumulative lifetime counter sum</s-text>
						</s-stack>
					</s-box>

					<!-- Card 2: 30-Day Avg Volume -->
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text color="subdued" style="font-weight: 500; font-size: 0.875rem; text-transform: uppercase;">30-Day Daily Rate</s-text>
							<s-heading element="h2" style="font-size: 1.75rem; font-weight: 700; color: var(--p-color-text);">${avgVolumeStr}</s-heading>
							<s-text tone="subdued" style="font-size: 0.825rem;">Calculated rolling print velocity</s-text>
						</s-stack>
					</s-box>

					<!-- Card 3: Next Service Forecast -->
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="small">
							<s-text color="subdued" style="font-weight: 500; font-size: 0.875rem; text-transform: uppercase;">Next Service Projected</s-text>
							<s-heading element="h2" style="font-size: 1.5rem; font-weight: 700; color: var(--p-color-text);">${forecastLabel}</s-heading>
							${isFallback ? `
								<div style="background: var(--p-color-bg-surface-secondary); padding: 0.35rem 0.5rem; border-radius: 4px; border-left: 3px solid var(--p-color-border-attention);">
									<s-text style="color: var(--p-color-text-attention); font-size: 0.8rem; font-weight: 600; line-height: 1.2;">
										⚠ Fleet model fallback: insufficient local history.
									</s-text>
								</div>
							` : `
								<s-text tone="success" style="font-size: 0.825rem;">✔ Forecast generated from local print patterns</s-text>
							`}

							<!-- Methodology Disclosure -->
							<details style="margin-top:0.5rem; border-top:1px solid var(--p-color-border-subdued); padding-top:0.5rem;">
								<summary style="cursor:pointer; font-size:0.8rem; font-weight:500; color:var(--p-color-text-link, #008060);">
									How is this calculated?
								</summary>
								<div style="margin-top:0.5rem; padding:0.75rem; background:var(--p-color-bg-surface-secondary); border-radius:4px; font-size:0.8rem; line-height:1.5; color:var(--p-color-text-subdued);">
									${isFallback ? `
										<p><strong>Fallback Method:</strong> This machine has fewer than 30 days of telemetry history. The system uses a fleet-average daily velocity calculated across active machines of the same model class (<strong>${modelName}</strong>) to project consumable replacement cycles.</p>
									` : `
										<p><strong>Local print projection method:</strong> Daily print velocity is calculated as a rolling average of <strong>${Math.round(data.dailyAverageVolume)} pages/day</strong> based on actual readings.</p>
										<p>Remaining consumable capacity is divided by daily velocity to project the days until replacement threshold.</p>
									`}
								</div>
							</details>
						</s-stack>
					</s-box>
				</s-grid>

				<!-- Financial Performance Graph -->
				<s-section style="margin-top: 2rem;">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-stack gap="base">
							<div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
								<s-stack gap="none">
									<s-heading element="h3">Financial Performance (Proportional Profit & Loss)</s-heading>
									<s-text tone="subdued">Proportional monthly revenue generated vs consumable maintenance expense</s-text>
								</s-stack>
								
								<!-- Segmented view control - no button groups -->
								<div style="display: flex; gap: 0.25rem; background-color: var(--p-color-bg-surface-secondary, #f1f2f4); padding: 0.25rem; border-radius: 8px; border: 1px solid var(--p-color-border-subdued, #e1e3e5);">
									<s-button id="btn-toggle-realtime" variant="${viewMode === 'realtime' ? 'primary' : 'secondary'}" size="slim">Realtime</s-button>
									<s-button id="btn-toggle-cumulative" variant="${viewMode === 'cumulative' ? 'primary' : 'secondary'}" size="slim">Cumulative</s-button>
								</div>
							</div>

							<!-- Analytics Graph element -->
							<div style="margin-top: 1rem; width: 60%;">
								<opm-analytics-graph id="device-financial-graph" view-mode="${viewMode}"></opm-analytics-graph>
							</div>

							<!-- Proportional Disclaimer -->
							<div style="display: flex; align-items: center; justify-content: center; margin-top: 0.5rem;">
								<span style="font-size: 0.775rem; color: var(--p-color-text-subdued); text-align: center; font-style: italic;">
									Notice: Revenue and expenses are calculated proportionally based on machine-level print volume shares within the customer fleet. All figures in Nepali Rupees (NRs.).
								</span>
							</div>
						</s-stack>
					</s-box>
				</s-section>

				<!-- Section Tabs -->
				<s-section style="margin-top: 2rem;">
					<div style="display:flex; gap:0.25rem; border-bottom:1px solid var(--p-color-border-subdued); padding-bottom:0.25rem; margin-bottom:1rem; flex-wrap:wrap;">
						<button id="tab-consumables" class="tab-button" style="background:none; border:none; font-size:0.95rem; font-weight:600; padding:0.5rem 1rem; cursor:pointer; color:${activeDetailTab === 'consumables' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom:2px solid ${activeDetailTab === 'consumables' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Consumables
						</button>
						<button id="tab-readings" class="tab-button" style="background:none; border:none; font-size:0.95rem; font-weight:600; padding:0.5rem 1rem; cursor:pointer; color:${activeDetailTab === 'readings' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom:2px solid ${activeDetailTab === 'readings' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Readings History
						</button>
						<button id="tab-memos" class="tab-button" style="background:none; border:none; font-size:0.95rem; font-weight:600; padding:0.5rem 1rem; cursor:pointer; color:${activeDetailTab === 'memos' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom:2px solid ${activeDetailTab === 'memos' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Linked Memos
						</button>
						<button id="tab-service" class="tab-button" style="background:none; border:none; font-size:0.95rem; font-weight:600; padding:0.5rem 1rem; cursor:pointer; color:${activeDetailTab === 'service' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom:2px solid ${activeDetailTab === 'service' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Service History
						</button>
						<button id="tab-contract" class="tab-button" style="background:none; border:none; font-size:0.95rem; font-weight:600; padding:0.5rem 1rem; cursor:pointer; color:${activeDetailTab === 'contract' ? 'var(--p-color-text-link, #008060)' : 'var(--p-color-text-subdued)'}; border-bottom:2px solid ${activeDetailTab === 'contract' ? 'var(--p-color-text-link, #008060)' : 'transparent'};">
							Contract Terms
						</button>
					</div>

					<!-- Tab Content Grid -->
					${tabContentHtml}
				</s-section>
			</div>
		</s-page>
	`;
}

export function setupDeviceAnalyticsPageListeners() {
	bindDeviceAnalyticsDomEvents();
}

function bindDeviceAnalyticsDomEvents() {
	const realtimeBtn = document.getElementById('btn-toggle-realtime');
	const cumulativeBtn = document.getElementById('btn-toggle-cumulative');
	const graph = document.getElementById('device-financial-graph') as any;

	if (realtimeBtn && cumulativeBtn && graph) {
		realtimeBtn.addEventListener('click', () => {
			if (graphViewModeSignal.value !== 'realtime') {
				graphViewModeSignal.value = 'realtime';
				graph.viewMode = 'realtime';
				realtimeBtn.setAttribute('variant', 'primary');
				cumulativeBtn.setAttribute('variant', 'secondary');
			}
		});

		cumulativeBtn.addEventListener('click', () => {
			if (graphViewModeSignal.value !== 'cumulative') {
				graphViewModeSignal.value = 'cumulative';
				graph.viewMode = 'cumulative';
				realtimeBtn.setAttribute('variant', 'secondary');
				cumulativeBtn.setAttribute('variant', 'primary');
			}
		});
	}

	// Dynamic tab click listeners
	const tabIds = ['consumables', 'readings', 'memos', 'service', 'contract'];
	tabIds.forEach(tabKey => {
		const tabBtn = document.getElementById(`tab-${tabKey}`);
		if (tabBtn) {
			tabBtn.addEventListener('click', () => {
				activeDetailTabSignal.value = tabKey;
			});
		}
	});

	// Populate data elements on the active tab
	const assetId = selectedAnalyticsAssetIdSignal.value;
	const data = deviceAnalyticsDataSignal.value;
	if (assetId && data) {
		populateActiveTab(assetId, data);
	}
}

async function populateActiveTab(assetId: string, data: any) {
	const activeTab = activeDetailTabSignal.value;
	if (activeTab === 'consumables') {
		const table = document.getElementById('device-yield-table') as any;
		if (table && data) {
			table.columns = [
				{
					key: 'installDate',
					header: 'Installed Date',
					sortable: true,
					sortType: 'string',
					render: (row: any) => new Date(row.installDate).toLocaleDateString('en-GB'),
				},
				{
					key: 'endDate',
					header: 'Replaced Date',
					render: (row: any) => row.endDate ? new Date(row.endDate).toLocaleDateString('en-GB') : '<s-badge tone="success">Active</s-badge>',
				},
				{ key: 'consumableCode', header: 'Part Code', sortable: true, sortType: 'string' },
				{ key: 'consumableName', header: 'Description', sortable: true, sortType: 'string' },
				{
					key: 'startPageCount',
					header: 'Start Volume',
					alignment: 'right',
					render: (row: any) => row.startPageCount.toLocaleString() + ' pgs',
				},
				{
					key: 'endPageCount',
					header: 'End Volume',
					alignment: 'right',
					render: (row: any) => row.endPageCount ? row.endPageCount.toLocaleString() + ' pgs' : '<span style="color:var(--p-color-text-subdued);">Running</span>',
				},
				{
					key: 'yieldPages',
					header: 'Yield',
					alignment: 'right',
					render: (row: any) => row.yieldPages ? `<strong>${row.yieldPages.toLocaleString()} pgs</strong>` : '<span style="color:var(--p-color-text-subdued);">Calculating</span>',
				},
			];
			table.items = data.consumableHistory || [];
		}
	} else if (activeTab === 'readings') {
		const readingsTable = document.getElementById('asset360-readings-table') as any;
		if (readingsTable) {
			const pageTypes = pageTypesSignal.value || [];
			const ptMap = new Map(pageTypes.map(pt => [pt.id, pt.displayName]));

			readingsTable.columns = [
				{
					key: 'createdAt',
					header: 'Date & Time',
					sortable: true,
					sortType: 'string',
					render: (row: any) => new Date(row.createdAt).toLocaleString('en-GB')
				},
				{ key: 'reporterId', header: 'Reporter', sortable: true, sortType: 'string' },
				{
					key: 'counters',
					header: 'Counter Volumes',
					render: (row: any) => {
						const cnts = row.counters || {};
						return Object.entries(cnts).map(([ptId, val]) => {
							const name = ptMap.get(ptId) || ptId;
							return `<div style="font-size:0.875rem;">${name}: <strong>${(val as number).toLocaleString()}</strong></div>`;
						}).join('');
					}
				},
				{
					key: 'status',
					header: 'Status',
					render: (row: any) => {
						if (row.isOverridden) {
							return `<s-badge tone="attention">OVERRIDDEN</s-badge>`;
						}
						return `<s-badge tone="success">ACTIVE</s-badge>`;
					}
				},
				{
					key: 'photoUrl',
					header: 'Evidence',
					render: (row: any) => {
						if (row.photoUrl) {
							return `<a href="${row.photoUrl}" target="_blank" style="color:var(--p-color-text-link); text-decoration:underline; font-size:0.875rem;">View Photo</a>`;
						}
						return `<span style="font-size:0.875rem; color:var(--p-color-text-subdued);">None</span>`;
					}
				}
			];

			try {
				const res = await fetch(`/api/v1/assets/${assetId}/readings`, {
					headers: getAuthHeaders(),
				});
				if (res.ok) {
					readingsTable.items = await res.json();
				} else {
					readingsTable.items = [];
				}
			} catch (err) {
				console.error('Failed to fetch readings for Asset 360', err);
				readingsTable.items = [];
			}
		}
	} else if (activeTab === 'memos') {
		const memosTable = document.getElementById('asset360-memos-table') as any;
		if (memosTable) {
			memosTable.columns = [
				{ key: 'memoNumber', header: 'Memo #', sortable: true, sortType: 'string', render: (r: any) => `<strong>${r.memoNumber}</strong>` },
				{ key: 'memoType', header: 'Type', render: (r: any) => `<s-badge tone="${r.memoType === 'revenue' ? 'success' : 'attention'}">${r.memoType.toUpperCase()}</s-badge>` },
				{ key: 'periodStart', header: 'Period', render: (r: any) => `${r.periodStart} → ${r.periodEnd}` },
				{
					key: 'amount',
					header: 'Total',
					render: (r: any) => {
						const total = (r.items || []).reduce((acc: number, i: any) => acc + (i.amount || 0), 0);
						return `<strong>NRs. ${total.toFixed(2)}</strong>`;
					}
				},
			];

			try {
				const res = await fetch('/api/v1/internal-memos', { headers: getAuthHeaders() });
				if (res.ok) {
					const allMemos = await res.json();
					memosTable.items = allMemos.filter((m: any) => m.assetId === assetId);
				} else {
					memosTable.items = [];
				}
			} catch (err) {
				console.error('Failed to fetch memos for Asset 360', err);
				memosTable.items = [];
			}
		}
	} else if (activeTab === 'service') {
		const serviceTable = document.getElementById('asset360-service-table') as any;
		const healthPanel = document.getElementById('asset-health-metrics-panel');
		const timelineContainer = document.getElementById('asset360-service-timeline-container');

		if (serviceTable) {
			serviceTable.columns = [
				{ key: 'createdAt', header: 'Date', sortable: true, sortType: 'string', render: (r: any) => new Date(r.createdAt).toLocaleString('en-GB') },
				{
					key: 'action',
					render: (r: any) => {
						const tone = r.action === 'INSERT' ? 'success' : r.action === 'UPDATE' ? 'attention' : 'critical';
						return `<s-badge tone="${tone}">${r.action}</s-badge>`;
					}
				},
				{ key: 'actorId', header: 'Performed By', render: (r: any) => r.actorId || 'System' }
			];

			try {
				const res = await fetch(`/api/v1/audit-logs?tableName=assets&recordId=${assetId}`, { headers: getAuthHeaders() });
				if (res.ok) {
					serviceTable.items = await res.json();
				} else {
					const allRes = await fetch(`/api/v1/audit-logs`, { headers: getAuthHeaders() });
					if (allRes.ok) {
						const allLogs = await allRes.json();
						serviceTable.items = allLogs.filter((l: any) => l.tableName === 'assets' && l.recordId === assetId);
					} else {
						serviceTable.items = [];
					}
				}
			} catch (err) {
				console.error('Failed to fetch service history', err);
				serviceTable.items = [];
			}
		}

		try {
			let userMap = new Map<string, string>();
			try {
				const usersRes = await fetch('/api/v1/users?limit=1000', { headers: getAuthHeaders() });
				if (usersRes.ok) {
					const users = await usersRes.json();
					userMap = new Map(users.map((u: any) => [u.id, u.email]));
				}
			} catch (err) {
				console.error('Failed to fetch users', err);
			}

			const ticketsRes = await fetch(`/api/v1/tickets?assetId=${assetId}&page=1&pageSize=100`, { headers: getAuthHeaders() });
			if (!ticketsRes.ok) {
				throw new Error('Failed to fetch tickets');
			}
			const ticketsData = await ticketsRes.json();
			const tickets = ticketsData.items || [];

			const sessionsPromises = tickets.map(async (t: any) => {
				try {
					const sRes = await fetch(`/api/v1/tickets/${t.id}/sessions`, { headers: getAuthHeaders() });
					return sRes.ok ? await sRes.json() : [];
				} catch {
					return [];
				}
			});

			const partsPromises = tickets.map(async (t: any) => {
				try {
					const pRes = await fetch(`/api/v1/parts-recommendations?ticketId=${t.id}`, { headers: getAuthHeaders() });
					return pRes.ok ? await pRes.json() : [];
				} catch {
					return [];
				}
			});

			const [allSessionsLists, allPartsLists] = await Promise.all([
				Promise.all(sessionsPromises),
				Promise.all(partsPromises)
			]);

			const sessions = allSessionsLists.flat();
			const parts = allPartsLists.flat();

			const totalTickets = tickets.length;

			const resolvedClosedTickets = tickets.filter((t: any) => t.status === 'resolved' || t.status === 'closed');
			let mttrText = 'N/A';
			if (resolvedClosedTickets.length > 0) {
				let totalMs = 0;
				resolvedClosedTickets.forEach((t: any) => {
					const start = new Date(t.createdAt).getTime();
					const end = t.resolvedAt ? new Date(t.resolvedAt).getTime() : new Date(t.updatedAt).getTime();
					totalMs += (end - start);
				});
				const avgHours = totalMs / (1000 * 60 * 60 * resolvedClosedTickets.length);
				if (avgHours < 24) {
					mttrText = `${avgHours.toFixed(1)} hrs`;
				} else {
					mttrText = `${(avgHours / 24).toFixed(1)} days`;
				}
			}

			const openTickets = tickets.filter((t: any) => t.status !== 'resolved' && t.status !== 'closed');
			
			const repeats: Array<{ name: string; count: number }> = [];
			const ninetyDaysAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
			const partsByConsumable = new Map<string, { name: string; dates: number[] }>();

			parts.forEach((p: any) => {
				const dateMs = new Date(p.approvedAt || p.createdAt).getTime();
				if (dateMs >= ninetyDaysAgo && (p.status === 'approved' || p.status === 'installed' || p.status === 'completed')) {
					const code = p.consumableCode || p.consumableId;
					const name = p.consumableName || p.consumableCode || 'Unknown Part';
					if (!partsByConsumable.has(code)) {
						partsByConsumable.set(code, { name, dates: [] });
					}
					partsByConsumable.get(code)!.dates.push(dateMs);
				}
			});

			for (const info of partsByConsumable.values()) {
				if (info.dates.length > 1) {
					repeats.push({ name: info.name, count: info.dates.length });
				}
			}

			if (healthPanel) {
				let activeAlertsHtml = '';
				if (openTickets.length > 0) {
					activeAlertsHtml = openTickets.map((t: any) => `
						<div style="margin-top:0.5rem;">
							<s-banner tone="warning" title="Open Ticket: #${t.ticketNumber}">
								<p>Status: <strong>${t.status.toUpperCase()}</strong> | Priority: <strong>${t.priority.toUpperCase()}</strong></p>
								<p style="margin-top:0.25rem;">${escapeHtml(t.description)}</p>
							</s-banner>
						</div>
					`).join('');
				} else {
					activeAlertsHtml = `
						<s-text tone="subdued">No active alerts for this asset.</s-text>
					`;
				}

				let repeatsAlertsHtml = '';
				if (repeats.length > 0) {
					repeatsAlertsHtml = repeats.map(r => `
						<div style="margin-top:0.5rem;">
							<s-banner tone="critical" title="Repeated Part Failure Alert">
								<p>⚠️ <strong>${escapeHtml(r.name)}</strong> was replaced <strong>${r.count}</strong> times in the last 90 days. Investigate machine wear or feed mechanism.</p>
							</s-banner>
						</div>
					`).join('');
				} else {
					repeatsAlertsHtml = `
						<s-text tone="subdued">No repeating part failures detected.</s-text>
					`;
				}

				healthPanel.innerHTML = `
					<s-grid gridTemplateColumns="1fr 1fr" gap="base" style="margin-bottom:1rem;">
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Total Lifetime Tickets</s-text>
								<s-text style="font-weight:700; font-size:1.5rem; margin-top:0.25rem;">${totalTickets}</s-text>
							</s-stack>
						</s-box>
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-stack gap="none">
								<s-text tone="subdued" style="font-size:0.75rem; text-transform:uppercase; font-weight:600;">Mean Time to Resolution (MTTR)</s-text>
								<s-text style="font-weight:700; font-size:1.5rem; margin-top:0.25rem;">${mttrText}</s-text>
							</s-stack>
						</s-box>
					</s-grid>
					<s-grid gridTemplateColumns="1fr 1fr" gap="base">
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-heading element="h4">Active Alerts</s-heading>
							${activeAlertsHtml}
						</s-box>
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<s-heading element="h4">Repeat Replacements Check</s-heading>
							${repeatsAlertsHtml}
						</s-box>
					</s-grid>
				`;
			}

			// 1. Construct Replaced Parts Yield Table
			const historyItems = data.consumableHistory || [];
			const catalogConsumables = consumablesSignal.value || [];

			let partsTableHtml = '';
			if (historyItems.length === 0) {
				partsTableHtml = `
					<div style="padding: 1rem; text-align: center; border: 1px dashed var(--p-color-border-subdued,#e1e3e5); border-radius: 6px;">
						<s-text tone="subdued">No historical parts replacements recorded for this asset.</s-text>
					</div>
				`;
			} else {
				partsTableHtml = `
					<s-table>
						<s-table-header-row>
							<s-table-header>Install Date</s-table-header>
							<s-table-header>Part Details</s-table-header>
							<s-table-header>Install Counter</s-table-header>
							<s-table-header>Removal Counter</s-table-header>
							<s-table-header>Yield Achieved</s-table-header>
							<s-table-header>Status</s-table-header>
						</s-table-header-row>
						<s-table-body>
							${historyItems.map((h: any) => {
								const installDateStr = new Date(h.installDate).toLocaleDateString('en-GB');
								const catalogItem = catalogConsumables.find((c: any) => c.code === h.consumableCode);
								const ratedYield = catalogItem ? catalogItem.printYield : 0;
								
								const startVolume = h.startPageCount ? h.startPageCount.toLocaleString() + ' pgs' : '0 pgs';
								const endVolume = h.endPageCount ? h.endPageCount.toLocaleString() + ' pgs' : '<span style="color:var(--p-color-text-subdued);">Running</span>';
								
								let yieldText = 'Calculating';
								let statusHtml = '<s-badge tone="info">RUNNING</s-badge>';
								
								if (h.yieldPages !== null && h.yieldPages !== undefined) {
									yieldText = `${h.yieldPages.toLocaleString()} pgs`;
									if (ratedYield > 0) {
										const pct = Math.round((h.yieldPages / ratedYield) * 100);
										let tone: 'success' | 'attention' | 'critical' = 'success';
										if (pct < 60) tone = 'critical';
										else if (pct < 85) tone = 'attention';
										statusHtml = `<s-badge tone="${tone}">${pct}% Yield</s-badge>`;
									} else {
										statusHtml = '<s-badge tone="success">COMPLETED</s-badge>';
									}
								}
								
								return `
									<s-table-row>
										<s-table-cell>${installDateStr}</s-table-cell>
										<s-table-cell>
											<s-stack gap="none">
												<s-text style="font-weight: 600;">${escapeHtml(h.consumableName)}</s-text>
												<s-text tone="subdued" style="font-size: 0.8rem;">Code: ${escapeHtml(h.consumableCode)}</s-text>
											</s-stack>
										</s-table-cell>
										<s-table-cell>${startVolume}</s-table-cell>
										<s-table-cell>${endVolume}</s-table-cell>
										<s-table-cell><strong>${yieldText}</strong></s-table-cell>
										<s-table-cell>${statusHtml}</s-table-cell>
									</s-table-row>
								`;
							}).join('')}
						</s-table-body>
					</s-table>
				`;
			}

			// 2. Construct Collapsible Tickets Accordion
			let ticketsAccordionHtml = '';
			if (tickets.length === 0) {
				ticketsAccordionHtml = `
					<div style="padding: 1rem; text-align: center; border: 1px dashed var(--p-color-border-subdued,#e1e3e5); border-radius: 6px;">
						<s-text tone="subdued">No operational tickets logged for this asset.</s-text>
					</div>
				`;
			} else {
				ticketsAccordionHtml = tickets.map((t: any) => {
					const openedDateStr = new Date(t.createdAt).toLocaleDateString('en-GB');
					
					const statusTone = t.status === 'open' ? 'attention' : t.status === 'resolved' || t.status === 'closed' ? 'success' : 'info';
					const priorityTone = t.priority === 'critical' || t.priority === 'high' ? 'critical' : 'info';
					
					// Find sessions for this specific ticket
					const ticketSessions = sessions.filter((s: any) => s.ticketId === t.id);
					
					// Render sessions detail
					const sessionsHtml = ticketSessions.length === 0 
						? `<s-text tone="subdued" style="font-style: italic;">No technician sessions logged for this ticket yet.</s-text>`
						: ticketSessions.map((s: any) => {
							const date = new Date(s.createdAt).toLocaleString('en-GB');
							const techName = escapeHtml(userMap.get(s.technicianId) || s.technicianId || 'Unknown Tech');
							const durationHours = s.actualHours || 0;
							const notes = escapeHtml(s.summary || s.notes || 'No summary notes logged.');
							
							const photoUrls = s.photoUrls || [];
							const photosHtml = photoUrls.length > 0 ? `
								<div style="margin-top: 0.5rem;">
									<s-text tone="subdued" style="font-size: 0.8rem; font-weight: 600; display: block; margin-bottom: 0.25rem;">Attached Photos</s-text>
									<div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
										${photoUrls.map((url: string) => `
											<a href="${url}" target="_blank" style="display: block; width: 70px; height: 70px; border-radius: 4px; overflow: hidden; border: 1px solid var(--p-color-border-subdued,#e1e3e5); cursor: zoom-in;">
												<img src="${url}" style="width: 100%; height: 100%; object-fit: cover;" />
											</a>
										`).join('')}
									</div>
								</div>
							` : '';

							return `
								<div style="background: var(--p-color-bg-surface,#ffffff); padding: 0.75rem 1rem; border: 1px solid var(--p-color-border-subdued,#e1e3e5); border-radius: 6px; margin-top: 0.5rem;">
									<s-stack gap="small">
										<div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;">
											<s-text style="font-size: 0.875rem;">Visit by <strong>${techName}</strong></s-text>
											<s-text tone="subdued" style="font-size: 0.8rem;">${date} · Duration: <strong>${durationHours} hrs</strong></s-text>
										</div>
										<p style="margin: 0; font-size: 0.875rem; line-height: 1.5; color: var(--p-color-text,#202223); font-style: italic;">
											&ldquo;${notes}&rdquo;
										</p>
										${photosHtml}
									</s-stack>
								</div>
							`;
						}).join('');

					return `
						<details style="margin-bottom: 0.75rem; border: 1px solid var(--p-color-border-subdued,#e1e3e5); border-radius: 6px; background: var(--p-color-bg-surface,#ffffff); overflow: hidden;">
							<summary style="padding: 1rem; cursor: pointer; display: flex; justify-content: space-between; align-items: center; list-style: none; user-select: none;">
								<div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
									<s-text style="font-weight: 600; font-size: 0.95rem; color: var(--p-color-text-link,#008060);">Ticket #${escapeHtml(t.ticketNumber)}</s-text>
									<s-text tone="subdued" style="font-size: 0.85rem;">Opened: ${openedDateStr}</s-text>
									<s-badge tone="${statusTone}">${t.status.toUpperCase()}</s-badge>
									<s-badge tone="${priorityTone}">${t.priority.toUpperCase()}</s-badge>
								</div>
								<s-text tone="subdued" style="font-size: 1rem; font-weight: 600;">▼</s-text>
							</summary>
							<div style="padding: 1.25rem; border-top: 1px solid var(--p-color-border-subdued,#e1e3e5); background: var(--p-color-bg-surface-secondary,#f9fafb);">
								<s-stack gap="base">
									<!-- Description -->
									<div>
										<s-text style="font-weight: 600; font-size: 0.85rem; text-transform: uppercase; color: var(--p-color-text-subdued); display: block; margin-bottom: 0.25rem;">Problem Description</s-text>
										<p style="margin: 0; font-size: 0.9rem; line-height: 1.5; white-space: pre-wrap;">${escapeHtml(t.description)}</p>
									</div>

									<!-- Visit Logs -->
									<div>
										<s-text style="font-weight: 600; font-size: 0.85rem; text-transform: uppercase; color: var(--p-color-text-subdued); display: block; margin-bottom: 0.5rem;">Technician Visit Logs</s-text>
										${sessionsHtml}
									</div>
								</s-stack>
							</div>
						</details>
					`;
				}).join('');
			}

			if (timelineContainer) {
				timelineContainer.innerHTML = `
					<s-stack gap="base">
						<!-- Replaced Parts Table Card -->
						<s-box padding="base" background="surface" border="base" borderRadius="base">
							<div style="margin-bottom: 1rem;">
								<s-heading element="h3">Parts Replaced & Yield Performance</s-heading>
								<s-text tone="subdued">Detailed log of replaced consumables, counter readings, and historical lifespan yields.</s-text>
							</div>
							${partsTableHtml}
						</s-box>

						<!-- Expandable Tickets List Card -->
						<s-box padding="base" background="surface" border="base" borderRadius="base" style="margin-top: 0.5rem;">
							<div style="margin-bottom: 1rem;">
								<s-heading element="h3">Service Ticket Records</s-heading>
								<s-text tone="subdued">Expand each ticket to view technical remarks, session logs, and uploaded evidence photos.</s-text>
							</div>
							<div style="margin-top: 1rem;">
								${ticketsAccordionHtml}
							</div>
						</s-box>
					</s-stack>
				`;
			}
		} catch (err) {
			console.error('Failed to populate rich service history dashboard:', err);
			if (healthPanel) {
				healthPanel.innerHTML = `
					<s-banner tone="critical" title="Load Error">
						<p>Failed to compute health KPIs and chronological timeline.</p>
					</s-banner>
				`;
			}
			if (timelineContainer) {
				timelineContainer.innerHTML = `
					<s-text tone="subdued">Error loading timeline.</s-text>
				`;
			}
		}
	} else if (activeTab === 'contract') {
		const contractDiv = document.getElementById('asset360-contract-content');
		if (contractDiv && data) {
			try {
				const contractsRes = await fetch('/api/v1/contracts', { headers: getAuthHeaders() });
				if (contractsRes.ok) {
					const allContracts = await contractsRes.json();
					const assetCustomerId = data.customerId;
					const activeContracts = allContracts.filter((c: any) => {
						if (c.customerId !== assetCustomerId) return false;
						const today = new Date().toISOString().slice(0, 10);
						return today >= c.startDate && today <= c.endDate;
					});

					if (activeContracts.length === 0) {
						contractDiv.innerHTML = `<s-banner tone="attention"><p>No active contract found for this customer.</p></s-banner>`;
					} else {
						contractDiv.innerHTML = activeContracts.map((c: any) => `
							<s-box padding="base" background="surface" border="base" borderRadius="base" style="margin-bottom:1rem;">
								<s-stack gap="base">
									<div style="display:flex; justify-content:space-between; align-items:center;">
										<s-heading element="h3">Contract: ${c.billingCycle.toUpperCase()}</s-heading>
										<s-badge tone="success">ACTIVE</s-badge>
									</div>
									<s-text tone="subdued">Period: ${c.startDate} — ${c.endDate} | Scope: ${c.scopeType === 'per_machine' ? 'Per Machine' : 'Fleet Wide'}</s-text>
									<s-table>
										<s-table-header-row>
											<s-table-header>Page Type</s-table-header>
											<s-table-header>Base Volume</s-table-header>
											<s-table-header>Base Rate</s-table-header>
											<s-table-header>Overage Rate</s-table-header>
										</s-table-header-row>
										<s-table-body>
											${(c.rates || []).map((r: any) => `
												<s-table-row>
													<s-table-cell><strong>${r.pageTypeName || r.pageTypeId}</strong></s-table-cell>
													<s-table-cell>${Number(r.baseVolume).toLocaleString()} pgs</s-table-cell>
													<s-table-cell>NRs. ${Number(r.baseRate).toFixed(2)}</s-table-cell>
													<s-table-cell>NRs. ${Number(r.overageRate).toFixed(4)}</s-table-cell>
												</s-table-row>
											`).join('')}
										</s-table-body>
									</s-table>
								</s-stack>
							</s-box>
						`).join('');
					}
				}
			} catch (err) {
				console.error('Failed to fetch contract details', err);
				contractDiv.innerHTML = `<s-banner tone="critical"><p>Failed to load contract terms.</p></s-banner>`;
			}
		}
	}
}

export function unmountDeviceAnalyticsPage() {}

